/**
 * WP Quality Auditor — LLM audyt jakości wpisu WP w 4 wymiarach SRO.
 *
 * Uruchamiany PO SRO extraction — używa extracted_entity/attribute/query_frame jako kontekstu.
 * Prompt: thematic_prompts.step='wp_quality_audit' (gpt-4.1-mini, temp 0.1, json_schema).
 *
 * Wymiary:
 *  - entity_anchoring_score (0-100)
 *  - information_gain_score (0-100)
 *  - humanization_risk_score (0-100, 100=AI, 0=naturalne)
 *  - retrieval_ready (boolean, 5-punktowy test SRO sec 4.4)
 *
 * Output nadpisuje wp_posts.quality_recommendation (heurystyka staje się fallback).
 *
 * Koszt: ~$0.0025 per wpis (gpt-4.1-mini). 88 wpisów ~$0.22.
 */

import { supabase } from "@/integrations/supabase/client";
import { loadPrompt, fillTemplate } from "@/services/llm/promptLoader";
import { openaiProvider } from "@/services/llm/openaiProvider";
import { logPublic, logDebug, logError } from "@/services/contentEngine/contentLogger";

const PREFIX = "[WP-AUDIT]";
const DEFAULT_CONCURRENCY = 10;
const MAX_CONCURRENCY = 30;
const TRACE_MISMATCH_MAX_RETRIES = 1;

const QUALITY_AUDIT_SCHEMA: Record<string, unknown> = {
  type: "object",
  additionalProperties: false,
  required: [
    "entity_anchoring",
    "information_gain",
    "retrieval_ready",
    "humanization_risk",
    "quality_recommendation",
    "critical_issues",
    "overall_verdict",
    "seo_override_trace",
  ],
  properties: {
    entity_anchoring: {
      type: "object",
      additionalProperties: false,
      required: [
        "score",
        "entity_in_first_sentence",
        "entity_in_first_two_sentences",
        "pronoun_issues",
        "supporting_entities_in_first_100w",
        "reasoning",
      ],
      properties: {
        score: { type: "integer", minimum: 0, maximum: 100 },
        entity_in_first_sentence: { type: "boolean" },
        entity_in_first_two_sentences: { type: "boolean" },
        pronoun_issues: { type: "boolean" },
        supporting_entities_in_first_100w: { type: "integer", minimum: 0, maximum: 10 },
        reasoning: { type: "string" },
      },
    },
    information_gain: {
      type: "object",
      additionalProperties: false,
      required: [
        "score",
        "generic_serp_patterns_detected",
        "unique_angles_detected",
        "extractable_claims_count",
        "extractable_claims_examples",
        "reasoning",
      ],
      properties: {
        score: { type: "integer", minimum: 0, maximum: 100 },
        generic_serp_patterns_detected: {
          type: "array",
          items: { type: "string" },
          maxItems: 5,
        },
        unique_angles_detected: {
          type: "array",
          items: { type: "string" },
          maxItems: 5,
        },
        extractable_claims_count: { type: "integer", minimum: 0, maximum: 20 },
        extractable_claims_examples: {
          type: "array",
          items: { type: "string" },
          maxItems: 3,
        },
        reasoning: { type: "string" },
      },
    },
    retrieval_ready: {
      type: "object",
      additionalProperties: false,
      required: [
        "pass",
        "fpa_direct",
        "passage_extractable",
        "entity_presence",
        "supporting_entities_present",
        "format_frame_match",
        "failure_reasons",
      ],
      properties: {
        pass: { type: "boolean" },
        fpa_direct: { type: "boolean" },
        passage_extractable: { type: "boolean" },
        entity_presence: { type: "boolean" },
        supporting_entities_present: { type: "boolean" },
        format_frame_match: { type: "boolean" },
        failure_reasons: { type: "array", items: { type: "string" } },
      },
    },
    humanization_risk: {
      type: "object",
      additionalProperties: false,
      required: [
        "score",
        "detected_patterns",
        "ai_generic_openers",
        "ai_vocabulary_count",
        "filler_transitions_count",
        "reasoning",
      ],
      properties: {
        score: { type: "integer", minimum: 0, maximum: 100 },
        detected_patterns: {
          type: "array",
          maxItems: 10,
          items: {
            type: "object",
            additionalProperties: false,
            required: ["pattern", "quote", "severity"],
            properties: {
              pattern: { type: "string" },
              quote: { type: "string" },
              severity: {
                type: "string",
                enum: ["critical", "moderate", "minor"],
              },
            },
          },
        },
        ai_generic_openers: { type: "integer", minimum: 0 },
        ai_vocabulary_count: { type: "integer", minimum: 0 },
        filler_transitions_count: { type: "integer", minimum: 0 },
        reasoning: { type: "string" },
      },
    },
    quality_recommendation: {
      type: "string",
      enum: ["keep", "refurbish", "borderline"],
    },
    critical_issues: {
      type: "array",
      items: { type: "string" },
      maxItems: 5,
    },
    overall_verdict: { type: "string" },
    seo_override_trace: {
      type: "object",
      additionalProperties: false,
      required: [
        "phase_1_recommendation",
        "retrieval_partial",
        "seo_signals_observed",
        "override_reasoning",
      ],
      properties: {
        phase_1_recommendation: {
          type: "string",
          enum: ["keep", "refurbish", "borderline"],
        },
        retrieval_partial: {
          type: "integer",
          minimum: 0,
          maximum: 5,
        },
        seo_signals_observed: {
          type: "object",
          additionalProperties: false,
          required: [
            "organic_traffic",
            "top_3_count",
            "top_10_count",
            "top_50_count",
            "content_decay",
            "lost_keywords_count",
          ],
          properties: {
            organic_traffic: { type: "number" },
            top_3_count: { type: "number" },
            top_10_count: { type: "number" },
            top_50_count: { type: "number" },
            content_decay: { type: "boolean" },
            lost_keywords_count: { type: "number" },
          },
        },
        override_reasoning: { type: "string" },
      },
    },
  },
};

// LLM output parsed (zgodny ze schematem v4)
interface SeoOverrideTrace {
  phase_1_recommendation: "keep" | "refurbish" | "borderline";
  retrieval_partial: number; // 0-5
  seo_signals_observed: {
    organic_traffic: number;
    top_3_count: number;
    top_10_count: number;
    top_50_count: number;
    content_decay: boolean;
    lost_keywords_count: number;
  };
  override_reasoning: string;
}

interface ParsedAudit {
  entity_anchoring: { score: number };
  information_gain: { score: number };
  retrieval_ready: { pass: boolean };
  humanization_risk: { score: number };
  quality_recommendation: "keep" | "refurbish" | "borderline";
  critical_issues: string[];
  overall_verdict: string;
  seo_override_trace: SeoOverrideTrace;
}

export interface QualityAuditResult {
  success: boolean;
  entity_anchoring_score?: number;
  information_gain_score?: number;
  humanization_risk_score?: number;
  retrieval_ready?: boolean;
  quality_recommendation?: "keep" | "refurbish" | "borderline";
  critical_issues?: string[];
  overall_verdict?: string;
  raw?: Record<string, unknown>;
  compiled_prompt?: string;
  response_text?: string;
  tokens_in: number;
  tokens_out: number;
  cost_usd: number;
  error?: string;
}

interface AuditableRow {
  id: string;
  title: string;
  content_plaintext: string | null;
  extracted_entity: string | null;
  extracted_attribute: string | null;
  extracted_query_frame: string | null;
  first_paragraph_answer: string | null;
  supporting_entities: unknown;
  // M13 — SEO signals (Senuto) for prompt rules 5a-5d
  seo_organic_traffic?: number | null;
  seo_top_3_count?: number | null;
  seo_top_10_count?: number | null;
  seo_top_50_count?: number | null;
  seo_content_decay?: boolean | null;
  seo_lost_keywords_count?: number | null;
}

function extractFirstNWords(text: string, n: number): string {
  if (!text) return "";
  const words = text.trim().split(/\s+/);
  if (words.length <= n) return text.trim();
  return words.slice(0, n).join(" ");
}

async function auditOne(
  row: AuditableRow,
  template: string,
  modelId: string,
  temperature: number,
): Promise<QualityAuditResult> {
  if (!row.content_plaintext || row.content_plaintext.trim().length === 0) {
    return {
      success: false,
      error: "empty content_plaintext",
      tokens_in: 0,
      tokens_out: 0,
      cost_usd: 0,
    };
  }

  const supporting = Array.isArray(row.supporting_entities)
    ? (row.supporting_entities as string[])
    : [];

  const prompt = fillTemplate(template, {
    wp_title: row.title || "",
    wp_content_first_1500: extractFirstNWords(row.content_plaintext, 1500),
    extracted_entity: row.extracted_entity || "UNKNOWN",
    extracted_attribute: row.extracted_attribute || "UNKNOWN",
    extracted_query_frame: row.extracted_query_frame || "UNKNOWN",
    first_paragraph_answer: row.first_paragraph_answer || "",
    supporting_entities: JSON.stringify(supporting),
    // M13 SEO signals — 6 template vars (rule 5a-5d w prompcie)
    seo_organic_traffic: String(row.seo_organic_traffic ?? 0),
    seo_top_3_count: String(row.seo_top_3_count ?? 0),
    seo_top_10_count: String(row.seo_top_10_count ?? 0),
    seo_top_50_count: String(row.seo_top_50_count ?? 0),
    seo_content_decay: String(row.seo_content_decay ?? false),
    seo_lost_keywords_count: String(row.seo_lost_keywords_count ?? 0),
  });

  // Retry przy mismatch phase_2_recommendation vs quality_recommendation
  // (LLM musi być spójny: phase_2 == quality_recommendation).
  let lastError: string | undefined;
  let totalTokensIn = 0;
  let totalTokensOut = 0;
  let totalCost = 0;
  let lastResponseText = "";

  for (let attempt = 0; attempt <= TRACE_MISMATCH_MAX_RETRIES; attempt++) {
    try {
      const response = await openaiProvider.call(prompt, {
        model: modelId,
        temperature,
        jsonSchema: QUALITY_AUDIT_SCHEMA,
      });
      totalTokensIn += response.tokensInput;
      totalTokensOut += response.tokensOutput;
      totalCost += response.costUsd ?? 0;
      lastResponseText = response.text;

      const parsed = JSON.parse(response.text) as ParsedAudit;

      // =============================================
      // POST-PROCESSING (fail_count → recommendation) — kod decyduje, nie LLM
      // =============================================
      const ig = parsed.information_gain.score;
      const hr = parsed.humanization_risk.score;
      const ea = parsed.entity_anchoring.score;
      const rr = parsed.retrieval_ready.pass;

      const fail_count =
        (ig < 40 ? 1 : 0) +
        (hr >= 70 ? 1 : 0) +
        (ea < 40 ? 1 : 0) +
        (!rr ? 1 : 0);

      let finalReco: "keep" | "refurbish" | "borderline";
      if (fail_count === 0 && ig >= 60 && hr <= 50 && ea >= 60) {
        finalReco = "keep";
      } else if (fail_count >= 2) {
        finalReco = "refurbish";
      } else {
        finalReco = "borderline";
      }

      // SEO annotation prepend — informacyjne, nie zmienia reco
      const seoTraffic = row.seo_organic_traffic ?? 0;
      const seoTop3 = row.seo_top_3_count ?? 0;
      const seoTop10 = row.seo_top_10_count ?? 0;
      const hasSeoValue = seoTop3 >= 1 || seoTop10 >= 2 || seoTraffic >= 20;

      const finalIssues = [...parsed.critical_issues];
      if (hasSeoValue) {
        finalIssues.unshift(
          `🔒 RANKUJE (traffic=${seoTraffic}, top10=${seoTop10}, top3=${seoTop3}). Jeśli refurbish — URL zostaje (default refurbish flow). Ranking zachowany.`,
        );
      }

      return {
        success: true,
        entity_anchoring_score: ea,
        information_gain_score: ig,
        humanization_risk_score: hr,
        retrieval_ready: rr,
        quality_recommendation: finalReco,
        critical_issues: finalIssues,
        overall_verdict: parsed.overall_verdict,
        raw: {
          ...(parsed as unknown as Record<string, unknown>),
          post_proc_fail_count: fail_count,
          post_proc_final_reco: finalReco,
          post_proc_seo_annotated: hasSeoValue,
        },
        compiled_prompt: prompt,
        response_text: lastResponseText,
        tokens_in: totalTokensIn,
        tokens_out: totalTokensOut,
        cost_usd: totalCost,
      };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      lastError = msg;
      if (attempt < TRACE_MISMATCH_MAX_RETRIES) {
        logDebug(PREFIX, `Audit error attempt ${attempt + 1}: ${msg} — retrying`);
        continue;
      }
    }
  }

  return {
    success: false,
    error: lastError ?? "unknown",
    compiled_prompt: prompt,
    response_text: lastResponseText,
    tokens_in: totalTokensIn,
    tokens_out: totalTokensOut,
    cost_usd: totalCost,
  };
}

export async function auditQualityForWpPost(wpPostId: string): Promise<QualityAuditResult> {
  const prompt = await loadPrompt("wp_quality_audit");
  if (!prompt) {
    return {
      success: false,
      error: "Prompt wp_quality_audit nie jest aktywny w bazie",
      tokens_in: 0,
      tokens_out: 0,
      cost_usd: 0,
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: row } = await (supabase as any)
    .from("wp_posts")
    .select(
      "id, title, content_plaintext, extracted_entity, extracted_attribute, extracted_query_frame, first_paragraph_answer, supporting_entities, seo_organic_traffic, seo_top_3_count, seo_top_10_count, seo_top_50_count, seo_content_decay, seo_lost_keywords_count",
    )
    .eq("id", wpPostId)
    .maybeSingle();

  if (!row) {
    return {
      success: false,
      error: "Wpis nie istnieje",
      tokens_in: 0,
      tokens_out: 0,
      cost_usd: 0,
    };
  }

  const result = await auditOne(
    row as AuditableRow,
    prompt.template,
    prompt.modelId || "gpt-4.1-mini",
    prompt.temperature ?? 0.1,
  );

  if (result.success) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (supabase as any)
      .from("wp_posts")
      .update({
        entity_anchoring_score: result.entity_anchoring_score,
        information_gain_score: result.information_gain_score,
        humanization_risk_score: result.humanization_risk_score,
        retrieval_ready: result.retrieval_ready,
        quality_audit_issues: result.critical_issues ?? [],
        quality_audit_verdict: result.overall_verdict,
        quality_recommendation: result.quality_recommendation,
        quality_audited_at: new Date().toISOString(),
      })
      .eq("id", wpPostId);
  }

  return result;
}

export interface AuditBatchOptions {
  onProgress?: (done: number, total: number, costUsd: number) => void;
  onLog?: (
    type: "info" | "success" | "error" | "stage",
    message: string,
    debug?: Record<string, unknown>,
  ) => void;
  concurrency?: number;
  forceReaudit?: boolean;
}

export interface AuditBatchStats {
  processed: number;
  succeeded: number;
  failed: number;
  cost_usd: number;
  tokens_in: number;
  tokens_out: number;
  duration_ms: number;
  distribution: { keep: number; borderline: number; refurbish: number };
}

/**
 * Batch audit jakości dla website. Audytuje wpisy z SRO done (sro_extracted_at IS NOT NULL).
 * Concurrency 5 (równoległe LLM calls).
 */
export async function auditQualityForWebsite(
  websiteId: string,
  options: AuditBatchOptions = {},
): Promise<AuditBatchStats> {
  const t0 = Date.now();
  const concurrency = Math.max(1, Math.min(MAX_CONCURRENCY, options.concurrency ?? DEFAULT_CONCURRENCY));
  const forceReaudit = options.forceReaudit ?? false;

  const prompt = await loadPrompt("wp_quality_audit");
  if (!prompt) {
    throw new Error("Prompt wp_quality_audit nie jest aktywny w bazie");
  }

  // Load eligible posts
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let q = (supabase as any)
    .from("wp_posts")
    .select(
      "id, title, content_plaintext, extracted_entity, extracted_attribute, extracted_query_frame, first_paragraph_answer, supporting_entities, quality_audited_at, seo_organic_traffic, seo_top_3_count, seo_top_10_count, seo_top_50_count, seo_content_decay, seo_lost_keywords_count",
    )
    .eq("website_id", websiteId)
    .not("sro_extracted_at", "is", null);

  if (!forceReaudit) {
    q = q.is("quality_audited_at", null);
  }

  const { data, error } = await q;
  if (error) throw error;

  const rows = (data || []) as Array<AuditableRow & { quality_audited_at: string | null }>;
  const total = rows.length;

  logPublic(PREFIX, `Start quality audit — ${total} wpisów (concurrency=${concurrency})`);
  options.onLog?.(
    "info",
    `▶ Quality audit: ${total} wpisów do przeanalizowania (concurrency=${concurrency})`,
  );

  const stats: AuditBatchStats = {
    processed: 0,
    succeeded: 0,
    failed: 0,
    cost_usd: 0,
    tokens_in: 0,
    tokens_out: 0,
    duration_ms: 0,
    distribution: { keep: 0, borderline: 0, refurbish: 0 },
  };

  const modelId = prompt.modelId || "gpt-4.1-mini";
  const temperature = prompt.temperature ?? 0.1;

  for (let i = 0; i < rows.length; i += concurrency) {
    const chunk = rows.slice(i, i + concurrency);
    const results = await Promise.all(
      chunk.map((row) => auditOne(row, prompt.template, modelId, temperature)),
    );

    for (let j = 0; j < chunk.length; j++) {
      const row = chunk[j];
      const r = results[j];
      stats.processed++;
      stats.cost_usd += r.cost_usd;
      stats.tokens_in += r.tokens_in;
      stats.tokens_out += r.tokens_out;

      if (r.success && r.quality_recommendation) {
        stats.succeeded++;
        stats.distribution[r.quality_recommendation]++;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error: updErr } = await (supabase as any)
          .from("wp_posts")
          .update({
            entity_anchoring_score: r.entity_anchoring_score,
            information_gain_score: r.information_gain_score,
            humanization_risk_score: r.humanization_risk_score,
            retrieval_ready: r.retrieval_ready,
            quality_audit_issues: r.critical_issues ?? [],
            quality_audit_verdict: r.overall_verdict,
            quality_recommendation: r.quality_recommendation,
            quality_audited_at: new Date().toISOString(),
          })
          .eq("id", row.id);
        if (updErr) {
          logError(PREFIX, `Update ${row.id} failed: ${updErr.message}`);
        }
      } else {
        stats.failed++;
        logError(PREFIX, `Audit failed for ${row.id}: ${r.error}`);
      }
    }

    const pct = total > 0 ? Math.round((stats.processed / total) * 100) : 100;
    if (total > 0 && (stats.processed % 10 === 0 || stats.processed === total)) {
      const lastOk = results
        .map((r, idx) => ({ r, row: chunk[idx] }))
        .reverse()
        .find((x) => x.r.success);
      options.onLog?.(
        "info",
        `  Audit progress ${pct}% (${stats.processed}/${total}) — keep=${stats.distribution.keep} borderline=${stats.distribution.borderline} refurbish=${stats.distribution.refurbish}, cost=$${stats.cost_usd.toFixed(4)}`,
        lastOk
          ? {
              sample_post_id: lastOk.row.id,
              sample_title: lastOk.row.title,
              sample_scores: {
                entity_anchoring: lastOk.r.entity_anchoring_score,
                information_gain: lastOk.r.information_gain_score,
                humanization_risk: lastOk.r.humanization_risk_score,
                retrieval_ready: lastOk.r.retrieval_ready,
                recommendation: lastOk.r.quality_recommendation,
              },
              sample_critical_issues: lastOk.r.critical_issues,
              sample_verdict: lastOk.r.overall_verdict,
              compiled_prompt: lastOk.r.compiled_prompt,
              response_text: lastOk.r.response_text,
            }
          : undefined,
      );
      logDebug(PREFIX, `progress ${stats.processed}/${total}`);
    }
    options.onProgress?.(stats.processed, total, stats.cost_usd);
  }

  stats.duration_ms = Date.now() - t0;

  logPublic(
    PREFIX,
    `Audit done ${stats.duration_ms}ms: ${stats.succeeded}/${stats.processed} succeeded, $${stats.cost_usd.toFixed(4)} (keep=${stats.distribution.keep} borderline=${stats.distribution.borderline} refurbish=${stats.distribution.refurbish})`,
  );
  options.onLog?.(
    "stage",
    `✓ Audit done ${(stats.duration_ms / 1000).toFixed(1)}s: ${stats.succeeded}/${total} succeeded, cost=$${stats.cost_usd.toFixed(4)} · keep=${stats.distribution.keep} borderline=${stats.distribution.borderline} refurbish=${stats.distribution.refurbish}`,
    { stats },
  );

  // Auto-recompute Decision Engine (non-blocking) — M15
  try {
    const { recomputeFinalActionsForWebsite } = await import("@/services/decisionEngine");
    options.onLog?.("info", "▶ Auto-recompute Decision Engine po audycie...");
    await recomputeFinalActionsForWebsite(websiteId, { onLog: options.onLog });
  } catch (err) {
    logError(PREFIX, `Auto-recompute Decision Engine failed: ${err instanceof Error ? err.message : String(err)}`);
  }

  return stats;
}
